<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('role.subtitle') }}</span>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--primary" @click="addDrawer = true">
          <v-icon small>mdi-plus</v-icon>
          {{ $t('role.newRole') }}
        </button>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          Rôles
          <span class="ov-count">{{ roles.length }}</span>
        </div>

        <div class="ov-filters">
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input
              v-model="search"
              type="text"
              class="ov-input"
              :placeholder="$t('role.search') || 'Rechercher un rôle...'"
            />
          </div>

          <div class="ov-toggle">
            <button
              type="button"
              class="ov-toggle__opt"
              :class="{ 'ov-toggle__opt--active': viewMode === 'table' }"
              title="Tableau"
              @click="viewMode = 'table'"
            >
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </button>
            <button
              type="button"
              class="ov-toggle__opt"
              :class="{ 'ov-toggle__opt--active': viewMode === 'grid' }"
              title="Cartes"
              @click="viewMode = 'grid'"
            >
              <v-icon small>mdi-view-grid-outline</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- VUE TABLEAU -->
      <v-data-table
        v-if="viewMode === 'table'"
        :headers="tableHeaders"
        :items="roles"
        :search="search"
        :loading="loading"
        loader-height="2"
        class="ov-table"
        :no-data-text="$t('role.noRoles') || 'Aucun rôle enregistré'"
        loading-text="Chargement en cours..."
      >
        <template v-slot:[`item.libelle`]="{ item }">
          <span class="rl-name">{{ item.libelle }}</span>
        </template>
        <template v-slot:[`item.code`]="{ item }">
          <span class="ov-badge ov-badge--navy ov-badge--plain">{{ item.code }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="ov-row-actions rl-actions">
            <button type="button" class="ov-icon-btn" :title="$t('role.editRole')" @click="openEditDialog(item)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>

      <!-- VUE GRILLE -->
      <div v-if="viewMode === 'grid'" class="ov-card__body">
        <div v-if="filteredRoles.length === 0" class="ov-empty">
          {{ $t('role.noRoles') || 'Aucun rôle enregistré' }}
        </div>
        <div v-else class="rl-grid">
          <RoleItem v-for="(role, r) in filteredRoles" :key="r" :role="role" @edit="openEditDrawer" />
        </div>
      </div>
    </div>

    <!-- ===== PANNEAU AJOUT RÔLE ===== -->
    <v-navigation-drawer v-model="addDrawer" right fixed temporary width="620" class="ov-panel rl-panel">
      <div class="ov-panel__head">
        <div>
          <div class="ov-card__kicker">{{ $t('role.newRole') }}</div>
          <div class="ov-card__title">{{ $t('role.addRoleTitle') }}</div>
        </div>
        <button type="button" class="ov-icon-btn" :title="$t('role.close')" @click="addDrawer = false">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div class="ov-panel__body">
        <div class="ov-row">
          <div class="ov-field">
            <label class="ov-label">{{ $t('role.roleDesignation') }}<span class="ov-required">*</span></label>
            <input
              v-model.trim="newRole.libelle"
              type="text"
              class="ov-input"
              :class="{ 'is-invalid': submitted && !newRole.libelle }"
              :placeholder="$t('role.roleDesignation')"
            />
            <div v-if="submitted && !newRole.libelle" class="ov-error">Ce champ est obligatoire.</div>
          </div>

          <div class="ov-field">
            <label class="ov-label">{{ $t('role.roleCode') }}<span class="ov-required">*</span></label>
            <input
              v-model.trim="newRole.code"
              type="text"
              class="ov-input"
              :class="{ 'is-invalid': submitted && !newRole.code }"
              :placeholder="$t('role.roleCode')"
            />
            <div v-if="submitted && !newRole.code" class="ov-error">Ce champ est obligatoire.</div>
          </div>
        </div>

        <div class="ov-field">
          <div class="rl-perm-head">
            <label class="ov-label">{{ $t('role.permissions') }}<span class="ov-required">*</span></label>
            <span class="ov-count">{{ permissionSelected.length }} / {{ permissionList.length }}</span>
          </div>

          <div class="rl-perm-tools">
            <div class="ov-search rl-perm-search">
              <v-icon small>mdi-magnify</v-icon>
              <input
                v-model="permissionSearch"
                type="text"
                class="ov-input"
                placeholder="Rechercher une permission..."
              />
            </div>
            <button
              v-if="filteredPermissions.length > 0"
              type="button"
              class="ov-btn ov-btn--secondary ov-btn--sm"
              @click="toggleAllFilteredPermissions"
            >
              {{ allFilteredPermissionsSelected ? 'Tout désélectionner' : 'Tout sélectionner' }}
            </button>
          </div>

          <div v-if="filteredPermissions.length === 0" class="ov-empty">Aucune permission disponible</div>
          <div v-else class="rl-perms">
            <button
              v-for="permission in filteredPermissions"
              :key="permission.code"
              type="button"
              class="rl-perm"
              :class="{ 'rl-perm--selected': isPermissionIn(permissionSelected, permission) }"
              @click="togglePermission(permission)"
            >
              <span class="rl-perm__check">
                <v-icon v-if="isPermissionIn(permissionSelected, permission)" x-small color="white">mdi-check</v-icon>
              </span>
              <span class="rl-perm__label">{{ permission.libelle }}</span>
            </button>
          </div>
          <div v-if="submitted && permissionSelected.length === 0" class="ov-error">
            {{ $t('role.attachPermissions') }}
          </div>
        </div>
      </div>

      <template #append>
        <div class="ov-panel__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="resetNewRole">
            <v-icon small>mdi-restore</v-icon>
            Réinitialiser
          </button>
          <button type="button" class="ov-btn ov-btn--primary" @click="submitNewRole">
            <v-icon small>mdi-content-save-outline</v-icon>
            {{ $t('role.save') }}
          </button>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- ===== PANNEAU ÉDITION RÔLE ===== -->
    <v-navigation-drawer v-model="editDrawer" right fixed temporary width="620" class="ov-panel rl-panel">
      <div class="ov-panel__head">
        <div>
          <div class="ov-card__kicker">{{ $t('role.editRole') }}</div>
          <div class="ov-card__title">{{ editRole.libelle || $t('role.editRoleTitle') }}</div>
        </div>
        <button type="button" class="ov-icon-btn" :title="$t('role.close')" @click="editDrawer = false">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div class="ov-panel__body">
        <div class="ov-row">
          <div class="ov-field">
            <label class="ov-label">{{ $t('role.roleDesignation') }}<span class="ov-required">*</span></label>
            <input
              v-model.trim="editRole.libelle"
              type="text"
              class="ov-input"
              :placeholder="$t('role.roleDesignation')"
            />
          </div>
          <div class="ov-field">
            <label class="ov-label">{{ $t('role.roleCode') }}<span class="ov-required">*</span></label>
            <input
              v-model.trim="editRole.code"
              type="text"
              class="ov-input"
              :placeholder="$t('role.roleCode')"
            />
          </div>
        </div>

        <div class="ov-field">
          <div class="rl-perm-head">
            <label class="ov-label">{{ $t('role.permissions') }}</label>
            <span class="ov-count">{{ editPermissionSelected.length }} / {{ permissionList.length }}</span>
          </div>

          <div class="ov-note rl-perm-note">
            <v-icon small>mdi-lightning-bolt-outline</v-icon>
            <span>Chaque permission cochée ou décochée est enregistrée immédiatement.</span>
          </div>

          <v-progress-linear
            v-if="loadingFunctionalities"
            indeterminate
            color="#159e72"
            height="3"
            class="rl-perm-loader"
          ></v-progress-linear>

          <div class="ov-search rl-perm-search rl-perm-search--full">
            <v-icon small>mdi-magnify</v-icon>
            <input
              v-model="editPermissionSearch"
              type="text"
              class="ov-input"
              placeholder="Rechercher une permission..."
            />
          </div>

          <div v-if="filteredEditPermissions.length === 0" class="ov-empty">Aucune permission disponible</div>
          <div v-else class="rl-perms" :class="{ 'rl-perms--loading': loadingFunctionalities }">
            <button
              v-for="permission in filteredEditPermissions"
              :key="permission.code"
              type="button"
              class="rl-perm"
              :class="{ 'rl-perm--selected': isPermissionIn(editPermissionSelected, permission) }"
              :disabled="loadingFunctionalities"
              @click="toggleEditPermission(permission)"
            >
              <span class="rl-perm__check">
                <v-icon v-if="isPermissionIn(editPermissionSelected, permission)" x-small color="white">mdi-check</v-icon>
              </span>
              <span class="rl-perm__label">{{ permission.libelle }}</span>
            </button>
          </div>
        </div>
      </div>

      <template #append>
        <div class="ov-panel__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="editDrawer = false">
            <v-icon small>mdi-close</v-icon>
            {{ $t('role.close') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" @click="submitEditRole">
            <v-icon small>mdi-content-save-outline</v-icon>
            {{ $t('role.save') }}
          </button>
        </div>
      </template>
    </v-navigation-drawer>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import RoleItem from "../widgets/RoleItem.vue";
import { getRoleUserApi } from "../services/roleApi";
import { getAllPermissionApi } from "@/modules/common/services/commonApi";
import axios from "axios";
import {
  API_CREATE_USER_ROLE,
  API_GET_FUNCTIONNALITY_BY_ROLE,
  API_ROLE_FUNCTIONALITIES,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "@/modules/messages/messageProcess";

export default {
  name: "RoleAdmin",
  components: { RoleItem },

  data() {
    return {
      roles: [],
      loading: false,
      search: "",
      viewMode: "grid",
      submitted: false,

      addDrawer: false,
      editDrawer: false,
      overlay: false,
      errorMsg: "",
      successMsg: "",

      permissionList: [],
      permissionSelected: [],
      permissionSearch: "",
      editPermissionSearch: "",
      permissionHeaders: [{ text: this.$t('role.label'), value: "libelle" }],

      newRole: { code: null, libelle: null, datasFunctionalities: [] },
      editRole: { code: "", libelle: "", datasFunctionalities: [] },
      editPermissionSelected: [],
      loadingFunctionalities: false,

      userRoleDataToSend: { datas: [] },
    };
  },

  computed: {
    tableHeaders() {
      return [
        { text: this.$t('role.roleDesignation') || "Désignation", value: "libelle" },
        { text: this.$t('role.roleCode') || "Code", value: "code" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ];
    },
    filteredRoles() {
      if (!this.search) return this.roles;
      const q = this.search.toLowerCase();
      return this.roles.filter(
        r => (r.libelle || "").toLowerCase().includes(q) || (r.code || "").toLowerCase().includes(q)
      );
    },
    filteredPermissions() {
      return this.filterPermissionList(this.permissionSearch);
    },
    filteredEditPermissions() {
      return this.filterPermissionList(this.editPermissionSearch);
    },
    allFilteredPermissionsSelected() {
      return (
        this.filteredPermissions.length > 0 &&
        this.filteredPermissions.every(p => this.isPermissionIn(this.permissionSelected, p))
      );
    },
  },

  methods: {
    // ===== HELPERS SÉLECTION DES PERMISSIONS (tuiles) =====
    filterPermissionList(term) {
      const list = this.permissionList || [];
      if (!term) return list;
      const q = term.toLowerCase();
      return list.filter(
        p => (p.libelle || "").toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q)
      );
    },

    isPermissionIn(list, permission) {
      return (list || []).some(p => p && permission && p.code === permission.code);
    },

    togglePermission(permission) {
      if (this.isPermissionIn(this.permissionSelected, permission)) {
        this.permissionSelected = this.permissionSelected.filter(p => p.code !== permission.code);
      } else {
        this.permissionSelected = [...this.permissionSelected, permission];
      }
    },

    toggleAllFilteredPermissions() {
      if (this.allFilteredPermissionsSelected) {
        const codes = this.filteredPermissions.map(p => p.code);
        this.permissionSelected = this.permissionSelected.filter(p => !codes.includes(p.code));
      } else {
        const missing = this.filteredPermissions.filter(p => !this.isPermissionIn(this.permissionSelected, p));
        this.permissionSelected = [...this.permissionSelected, ...missing];
      }
    },

    // Équivalent de l'événement @item-selected de l'ancien v-data-table
    toggleEditPermission(permission) {
      if (this.loadingFunctionalities) return;
      const value = !this.isPermissionIn(this.editPermissionSelected, permission);
      if (value) {
        this.editPermissionSelected = [...this.editPermissionSelected, permission];
      } else {
        this.editPermissionSelected = this.editPermissionSelected.filter(p => p.code !== permission.code);
      }
      this.onFunctionalityToggle({ item: permission, value });
    },

    async getUserRoleAvailable() {
      this.loading = true;
      const userRoles = await getRoleUserApi({}, this.$store.state.userAuthentified.token);
      this.roles = userRoles || [];
      this.loading = false;
    },

    async getFunctionnaliesAvailable() {
      const permissions = await getAllPermissionApi({}, this.$store.state.userAuthentified.token);
      this.permissionList = permissions || [];
    },

    resetNewRole() {
      this.submitted = false;
      this.newRole = { code: null, libelle: null, datasFunctionalities: [] };
      this.permissionSelected = [];
    },

    submitNewRole() {
      this.submitted = true;
      if (!this.newRole.libelle || !this.newRole.code) {
        this.errorMsg = this.$t("role.fillRequiredFields");
        showErrorMessage();
        return;
      }
      if (this.permissionSelected.length === 0) {
        this.errorMsg = this.$t("role.attachPermissions");
        showErrorMessage();
        return;
      }
      this.newRole.datasFunctionalities = this.permissionSelected;
      this.addNewRole();
    },

    async addNewRole() {
      this.userRoleDataToSend.datas = [this.newRole];
      this.overlay = true;
      this.addDrawer = false;
      await axios
        .post(API_CREATE_USER_ROLE, this.userRoleDataToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.getUserRoleAvailable();
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.userRoleDataToSend.datas = [];
          this.resetNewRole();
          this.overlay = false;
        });
    },

    async openEditDrawer(role) {
      this.editRole = Object.assign({}, role);
      this.editPermissionSelected = [];
      this.editDrawer = true;
      this.loadingFunctionalities = true;
      try {
        const response = await axios.post(
          API_GET_FUNCTIONNALITY_BY_ROLE,
          { data: { code: role.code } },
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        );
        if (response.data.status && response.data.status.code == 800) {
          const returnedItems = response.data.items || [];
          const returnedCodes = returnedItems.map(f => f.code || f.functionalityCode).filter(Boolean);
          this.editPermissionSelected = this.permissionList.filter(p => returnedCodes.includes(p.code));
        } else {
          this.errorMsg = (response.data.status && response.data.status.message) || 'Erreur chargement fonctionnalités';
          showErrorMessage();
        }
      } catch (e) {
        this.errorMsg = (e.response && e.response.data && e.response.data.status && e.response.data.status.message) || e.message || 'Erreur réseau';
        showErrorMessage();
      } finally {
        this.loadingFunctionalities = false;
      }
    },

    async onFunctionalityToggle({ item, value }) {
      const payload = {
        datas: [
          {
            functionalityCode: item.code,
            roleCode: this.editRole.code,
            isActive: value,
          },
        ],
      };
      const headers = { headers: HEADERS(this.$store.state.userAuthentified.token) };
      try {
        const response = await axios.post(API_ROLE_FUNCTIONALITIES, payload, headers);
        if (response.data.status && response.data.status.code == 800) {
          this.successMsg = response.data.status.message;
          showSuccessMessage();
        } else {
          this.errorMsg = (response.data.status && response.data.status.message) || 'Erreur';
          showErrorMessage();
          this._revertFunctionalityToggle(item, value);
        }
      } catch (e) {
        this.errorMsg = (e.response && e.response.data && e.response.data.status && e.response.data.status.message) || e.message || 'Erreur réseau';
        showErrorMessage();
        this._revertFunctionalityToggle(item, value);
      }
    },

    _revertFunctionalityToggle(item, value) {
      if (value) {
        this.editPermissionSelected = this.editPermissionSelected.filter(p => p.code !== item.code);
      } else {
        this.editPermissionSelected = [...this.editPermissionSelected, item];
      }
    },

    openEditDialog(role) {
      this.openEditDrawer(role);
    },

    async submitEditRole() {
      if (!this.editRole.libelle || !this.editRole.code) {
        this.errorMsg = this.$t("role.emptyFields");
        showErrorMessage();
        return;
      }
      this.userRoleDataToSend.datas = [{ code: this.editRole.code, libelle: this.editRole.libelle }];
      this.overlay = true;
      this.editDrawer = false;
      await axios
        .put(API_CREATE_USER_ROLE, this.userRoleDataToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.getUserRoleAvailable();
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.userRoleDataToSend.datas = [];
          this.overlay = false;
        });
    },
  },

  mounted() {
    this.getUserRoleAvailable();
    this.getFunctionnaliesAvailable();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

.rl-name {
  font-weight: 700;
  color: $ov-navy;
}

.rl-actions {
  justify-content: center;
}

.rl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

// ===== PANNEAU =====
.rl-panel {
  z-index: 200 !important;
}

// ===== PERMISSIONS (tuiles à cocher) =====
.rl-perm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .ov-label {
    margin-bottom: 7px;
  }
}

.rl-perm-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.rl-perm-search {
  flex: 1;
  min-width: 0;

  &--full {
    margin-bottom: 12px;
  }
}

.rl-perm-note {
  align-items: center;
  margin-bottom: 12px;
  font-size: 12px;
}

.rl-perm-loader {
  margin-bottom: 10px;
  border-radius: 999px;
}

.rl-perms {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  max-height: 420px;
  overflow-y: auto;
  padding: 2px;

  &--loading {
    opacity: 0.6;
  }
}

.rl-perm {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgba(21, 158, 114, 0.45);
  }

  &:disabled {
    cursor: wait;
  }

  &--selected {
    border-color: $ov-green;
    background: rgba(21, 158, 114, 0.06);
  }
}

.rl-perm__check {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 2px solid rgba(15, 23, 42, 0.2);
  transition: background 0.2s ease, border-color 0.2s ease;

  .rl-perm--selected & {
    background: $ov-green;
    border-color: $ov-green;
  }
}

.rl-perm__label {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: $ov-navy;
  word-break: break-word;
}

@media (max-width: 760px) {
  .rl-perms {
    grid-template-columns: 1fr;
  }

  .rl-perm-tools {
    flex-wrap: wrap;
  }
}
</style>
